// @flow

import get from 'lodash/get';
import type { AnyAction } from 'react-redux';
import { combineReducers } from 'redux';
import type { Reducer } from 'redux';
import { createReducer } from 'redux-act';
import {
  createSelector,
  createSelectorCreator,
  // $FlowFixMe[missing-export]
  lruMemoize,
} from 'reselect';
import shallowequal from 'shallowequal';

import {
  createResource,
  type FetchResponse,
  type Resource,
} from '@gandi/resource/redux';

import type { FullState, ResourceThunkAction } from '../../types';

export const DEFAULT_PER_PAGE = 10;

export type FetchDomainsParams = {
  '~domain_owner'?: string,
  '~fqdn'?: string,
  // XXX: to improve
  [key: string]: string | number,
};

export type ComputeLabelProps = FetchDomainsParams;

export type DomainSummary = {
  fqdn: string,
  fqdn_ulabel: string,
  id: string,
  sharing_space: {
    name: string,
    ...
  },
  ...
};

export type SummariesMeta = {
  filteredCount: number,
  ids: Array<DomainSummary['id']>,
  totalCount: number,
  ...
};

export type SummariesState = {
  data?: {
    [key: DomainSummary['id']]: DomainSummary,
    ...
  },
  [hash: string]: SummariesMeta,
  ...
};

export type State = {
  summaries: SummariesState,
  ...
};

// --------------------------------------------------------------- Configuration
const statePath = `simplehosting.domains`;
const baseUrl = `/domain/domains_v2`;

const hashString = (str: string): string =>
  String(
    str.split('').reduce(
      (prevHash, currVal) =>
        // eslint-disable-next-line no-bitwise
        (prevHash << 5) - prevHash + currVal.charCodeAt(0),
      0,
    ),
  );

export const computeLabel = (params: ComputeLabelProps): string =>
  hashString(
    JSON.stringify({
      ...params,
    }),
  );

// -------------------------------------------------------------------- Resource
export const resourceName = 'resources-simplehosting.domains';
export const resource: Resource<> = createResource(resourceName);

// --------------------------------------------------------------------- Actions
export type FetchDomainsAction<R> = (
  params?: Partial<FetchDomainsParams>,
  options?: { ... },
) => R;
export const fetch: FetchDomainsAction<ResourceThunkAction> =
  (params = {}, options = {}) =>
  dispatch => {
    const mergedParams = {
      ...params,
      nameserver: 'livedns',
      page: 1,
      per_page: DEFAULT_PER_PAGE,
      permission: 'dns:zone:write',
      sort_by: 'fqdn',
    };

    const label = computeLabel(params);

    return dispatch(
      // $FlowFixMe[cannot-spread-inexact]
      resource.fetch(baseUrl, {
        label,
        params: mergedParams,
        on200: (response: FetchResponse<>) => response,
        not200: (response: FetchResponse<>) => Promise.reject(response),
        ...options,
      }),
    );
  };

export type InvalidateDomainsResourceAction<R> = () => R;
export const invalidate: InvalidateDomainsResourceAction<ResourceThunkAction> =
  () => dispatch =>
    dispatch(resource.invalidate());

// -------------------------------------------------------------------- Reducers
export const initialState: State = {
  summaries: {},
};

export const handleFetchSuccess = (
  state: State['summaries'],
  payload: any,
): { [string]: SummariesMeta, data: any, ... } => ({
  ...state,
  data: payload.response.data.reduce(
    (old, current) => ({
      ...old,
      [current.id]: current,
    }),
    state.data,
  ),
  // $FlowFixMe[invalid-computed-prop] TODO: Fix type
  [payload.label]: {
    filteredCount: parseInt(
      payload.response.headers.get('x-filtered-count'),
      10,
    ),
    ids: payload.response.data.map(item => item.id),
    totalCount: parseInt(payload.response.headers.get('x-total-count'), 10),
  },
});

const summariesReducer = createReducer(
  {
    [resource.fetch.success.getType()]: handleFetchSuccess,
  },
  initialState.summaries,
);

export const reducer: Reducer<State, AnyAction> = combineReducers({
  summaries: summariesReducer,
});

// ------------------------------------------------------------------- Selectors
const selectRawState: (state: FullState) => State = state =>
  get(state, statePath, initialState);

const selectRawSummaries: (state: FullState) => SummariesState = state =>
  selectRawState(state).summaries;

const selectSummariesData: (
  state: FullState,
) => SummariesState['data'] = state => selectRawSummaries(state).data;

const createShallowEqualSelector = createSelectorCreator(
  lruMemoize,
  shallowequal,
);

export const selectComputedLabel: (
  state: FullState,
  props: ComputeLabelProps,
) => string = createShallowEqualSelector(
  [selectRawSummaries, (_, props) => computeLabel(props)],
  (raw, label) => label,
);

const selectSummariesMeta: (
  state: FullState,
  props: FetchDomainsParams,
) => ?SummariesMeta = createSelector(
  [selectRawSummaries, selectComputedLabel],
  (raw, label) => raw[label],
);

export const selectSummariesTotalCount: (
  state: FullState,
  props: FetchDomainsParams,
) => ?number = createSelector([selectSummariesMeta], meta =>
  meta ? meta.totalCount : undefined,
);

export const selectSummariesFilteredCount: (
  state: FullState,
  props: FetchDomainsParams,
) => ?number = createSelector([selectSummariesMeta], meta =>
  meta ? meta.filteredCount : undefined,
);

export const selectSummaries: (
  state: FullState,
  props: FetchDomainsParams,
) => ?Array<DomainSummary> = createSelector(
  [selectSummariesData, selectSummariesMeta],
  (data, meta) =>
    meta ? meta.ids.map(id => get(data, id)).filter(Boolean) : undefined,
);

// -------------------------------------------------------------- Resource flags

export const selectIsFetching = (
  state: FullState,
  props: FetchDomainsParams,
): boolean =>
  resource.selectIsFetching(state, selectComputedLabel(state, props));

export const selectIsLoaded = (
  state: FullState,
  props: FetchDomainsParams,
): boolean => resource.selectIsLoaded(state, selectComputedLabel(state, props));

export const selectIsFailure = (
  state: FullState,
  props: FetchDomainsParams,
): boolean =>
  resource.selectIsFailure(state, selectComputedLabel(state, props));

export const selectIsForbidden = (
  state: FullState,
  props: FetchDomainsParams,
): boolean =>
  resource.selectIsFailure(state, {
    label: selectComputedLabel(state, props),
    status: 403,
  });

export const selectIsToRefetch = (
  state: FullState,
  props: FetchDomainsParams,
): boolean =>
  resource.selectIsToRefetch(state, selectComputedLabel(state, props));
